import { DefaultError } from '@tanstack/query-core'
import {
  useQueries,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import axios from 'axios'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { CreditCardStatus } from 'src/models/creditCard.models'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { statisticsQueryKeys } from 'src/queries/queries.constants'
import {
  StatisticsKeys,
  StatisticsRequestsBody,
} from 'src/queries/statistics/statistics.queries.constants'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import {
  StatisticAggregateType,
  StatisticModelType,
  StatisticsRequest,
} from 'src/requests/statistics.request'

interface StatisticsResponse {
  statistics: Array<{ value: number }>
}

type StatisticsMappedResponse<T extends readonly StatisticsKeys[]> = {
  [K in T[number]]: number | undefined
}

interface UseQueryStatisticsOptions<T extends readonly StatisticsKeys[]>
  extends Omit<UseQueryOptions, 'queryKey'> {
  statisticsKeys: T
}

type UseStatisticQueryOptions = Array<UseQueryOptions<StatisticsResponse>>
type StatisticsMappedResults<T extends readonly StatisticsKeys[]> = {
  [K in T[number]]: UseQueryResult<number | undefined>
}
type UseStatisticsQueryResult<T extends readonly StatisticsKeys[]> = {
  results: StatisticsMappedResults<T>
  anyLoading: boolean
}

// TODO: the only use case for a useQuery request that's actually a POST, has to manually implement useQuery directly
/**
 * Fetches statistics for a list of statistic keys as separate queries with their own independent results.
 * If multiple statistics are needed, but they don't all need to be loaded at the same time, or it is more efficient
 * to query them as separate requests, this hook can be used.
 */
export const useQueryStatistics = <T extends readonly StatisticsKeys[]>(
  options: UseQueryStatisticsOptions<T>,
): UseStatisticsQueryResult<T> => {
  const requestConfig = useSelector(selectAuthedJsonApiRequestConfig)

  const combineStatistics = useCallback(
    (
      results: Array<UseQueryResult<StatisticsResponse>>,
    ): UseStatisticsQueryResult<T> => {
      const mappedResults = results.reduce((acc, result, i) => {
        return {
          ...acc,
          [options.statisticsKeys[i]]: {
            ...result,
            data: result.data?.statistics[0].value,
          },
        }
      }, {} as StatisticsMappedResults<T>)

      return {
        results: mappedResults,
        anyLoading: results.some((result) => result.isLoading),
      }
    },
    [options.statisticsKeys],
  )

  return useQueries<UseStatisticQueryOptions, UseStatisticsQueryResult<T>>({
    queries: options.statisticsKeys.map((statisticKey) => ({
      queryKey: statisticsQueryKeys.list([statisticKey]),
      queryFn: async () => {
        return await axios
          .post<StatisticsResponse>(
            '/statistics',
            { statistics: [StatisticsRequestsBody[statisticKey]] },
            requestConfig,
          )
          .then((response) => response.data)
      },
    })),
    combine: combineStatistics,
  })
}

// TODO: the only use case for a useQuery request that's actually a POST, has to manually implement useQuery directly
// TODO: how can we better ensure we aren't over-fetching statistics when there is overlap between the lists of keys?
/**
 * Fetches statistics for a list of statistic keys in a single request, relying on the backend to parallelize them.
 * The whole requests data will only be available once all statistics are fetched.
 */

// eslint-disable-next-line no-unused-vars
const useQueryBatchedStatistics = <T extends StatisticsKeys[]>(
  options: UseQueryStatisticsOptions<T>,
) => {
  const requestConfig = useSelector(selectAuthedJsonApiRequestConfig)

  const statisticsRequest: StatisticsRequest = useMemo(
    () => ({
      statistics: options.statisticsKeys.map((statisticName) => ({
        ...StatisticsRequestsBody[statisticName],
      })),
    }),
    [options.statisticsKeys],
  )

  const selectStatistics = useCallback(
    (data: StatisticsResponse) => {
      return data?.statistics.reduce((acc, { value }, i) => {
        return { ...acc, [options.statisticsKeys[i]]: value }
      }, {} as StatisticsMappedResponse<T>)
    },
    [options.statisticsKeys],
  )

  return useQuery<
    StatisticsResponse,
    DefaultError,
    StatisticsMappedResponse<T>
  >({
    queryKey: statisticsQueryKeys.list(options.statisticsKeys),
    queryFn: async () => {
      return await axios
        .post<StatisticsResponse>(
          '/statistics',
          statisticsRequest,
          requestConfig,
        )
        .then((response) => response.data)
    },
    select: selectStatistics,
  })
}

interface CreditCardStatisticsResponse {
  activeBudgetCount: number
  creditCardIds: string[]
  statistics: Array<{ value: number }>
}

export const useQueryCreditCardVCNStatistics = (
  creditCardIds: string[],
  type: StatisticModelType,
  enabled: boolean,
) => {
  const requestConfig = useSelector(selectAuthedJsonApiRequestConfig)
  return useQuery({
    queryKey: ['creditCardStatistics', 'vcn'],
    queryFn: async () => {
      const response = await axios.post<CreditCardStatisticsResponse>(
        `/statistics`,
        {
          statistics: creditCardIds.map((ccId) => ({
            agg: StatisticAggregateType.Count,
            type: type,
            query: {
              status: VirtualCardStatus.Active,
              creditCardId: ccId,
              all: true,
            },
          })),
        },
        requestConfig,
      )
      return {
        ...response.data,
        creditCardIds,
        statistics: response.data.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [creditCardIds[index]]: curr.value,
          }),
          {} as Record<string, number>,
        ),
      }
    },
    enabled,
  })
}

export const useQueryCreditCardBudgetsStatistics = (
  creditCardIds: string[],
  type: StatisticModelType,
  enabled: boolean,
) => {
  const requestConfig = useSelector(selectAuthedJsonApiRequestConfig)
  return useQuery({
    queryKey: ['creditCardStatistics', 'budgets'],
    queryFn: async () => {
      const response = await axios.post<CreditCardStatisticsResponse>(
        `/statistics`,
        {
          statistics: creditCardIds.map((ccId) => ({
            agg: StatisticAggregateType.Count,
            type: type,
            query: {
              status: CreditCardStatus.Active,
              parentCreditCard: ccId,
              all: true,
            },
          })),
        },
        requestConfig,
      )
      return {
        ...response.data,
        creditCardIds,
        statistics: response.data.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [creditCardIds[index]]: curr.value,
          }),
          {} as Record<string, number>,
        ),
      }
    },
    enabled,
  })
}
